import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'
import { useReadContract } from 'wagmi'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { computeCapInfo, formatCompactNumber } from '@/ui/molecules/cap-detail/capDetailUtils'

interface BorrowCapDetailProps {
  vaultAddress: Address
  decimals: number
  symbol: string
}

export function BorrowCapDetail({ vaultAddress, decimals, symbol }: BorrowCapDetailProps) {
  const { data: capsData } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'caps',
    chainId: E_LEND_CHAIN_ID,
    query: { enabled: !!vaultAddress },
  })

  const { data: totalBorrowsRaw } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'totalBorrows',
    chainId: E_LEND_CHAIN_ID,
    query: { enabled: !!vaultAddress },
  })

  const { data: totalAssetsRaw } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'totalAssets',
    chainId: E_LEND_CHAIN_ID,
    query: { enabled: !!vaultAddress },
  })

  const capInfo = useMemo(
    () => computeCapInfo(capsData?.[1], totalBorrowsRaw, capsData !== undefined, decimals),
    [capsData, totalBorrowsRaw, decimals],
  )

  // Available liquidity = totalAssets - totalBorrows (idle cash in vault)
  const availableLiquidity = useMemo(() => {
    if (totalAssetsRaw === undefined || totalBorrowsRaw === undefined) return undefined
    const liquidityBigInt = totalAssetsRaw > totalBorrowsRaw ? totalAssetsRaw - totalBorrowsRaw : 0n
    return NormalizedUnitNumber(formatUnits(liquidityBigInt, decimals))
  }, [totalAssetsRaw, totalBorrowsRaw, decimals])

  if ('isLoading' in capInfo || availableLiquidity === undefined) return null

  // The actual borrowable amount is the lesser of cap remaining and available liquidity
  const availableToBorrow = capInfo.isUnlimited
    ? availableLiquidity
    : availableLiquidity.lt(capInfo.remaining)
      ? availableLiquidity
      : capInfo.remaining

  return (
    <TransactionOverviewDetailsItem label="Available to Borrow">
      <span className="font-medium">
        {formatCompactNumber(availableToBorrow.toNumber())} {symbol}
      </span>
    </TransactionOverviewDetailsItem>
  )
}
